import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
  FaHandHoldingHeart, FaCreditCard, FaHeart, FaInfoCircle, 
  FaShieldAlt, FaChild,
  FaHandsHelping, FaMedkit, FaGraduationCap, FaHome,
  FaCheckCircle, FaArrowRight, FaArrowLeft,
  FaEye, FaDonate
} from 'react-icons/fa';
import { LanguageContext } from '../LanguageContext';
import './Donner.css';

const Donner = () => {
  const [customAmount, setCustomAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState("100");
  const [showCustom, setShowCustom] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);
  const [activeTab, setActiveTab] = useState('shares');
  const MySwal = withReactContent(Swal);
  const { language } = useContext(LanguageContext) || { language: 'ar' };

  const isRtl = language === 'ar';
  const ArrowIcon = isRtl ? FaArrowLeft : FaArrowRight;

  const translations = useMemo(() => ({
    heroTitle: {
      ar: "دائرة السند",
      fr: "Cercle de Soutien",
      en: "Circle of Support"
    },
    heroSubtitle: {
      ar: "نادي الكفلاء - معاً نصنع الأمل",
      fr: "Club des Parrains - Ensemble, créons l'espoir",
      en: "Sponsors Club - Together, We Create Hope"
    },
    heroDescription: {
      ar: "انضم إلى دائرة الثقة وكن كفيلاً دائماً لطفل يحتاج إلى رعايتك. مساهمتك الشهرية البسيطة تصنع فرقاً كبيراً في حياة أطفالنا.",
      fr: "Rejoignez notre cercle de confiance et devenez un parrain permanent pour un enfant qui a besoin de vous. Votre contribution mensuelle fait une grande différence.",
      en: "Join our circle of trust and become a permanent sponsor for a child in need. Your monthly contribution makes a huge difference."
    },
    sharesTitle: {
      ar: "أسهم الكفالة",
      fr: "Parts de Parrainage",
      en: "Sponsorship Shares"
    },
    sharesSubtitle: {
      ar: "اختر سهمك في رعاية أطفالنا",
      fr: "Choisissez votre part dans le soin de nos enfants",
      en: "Choose your share in caring for our children"
    },
    shareMilk: {
      ar: "سهم الحليب",
      fr: "Part du Lait",
      en: "Milk Share"
    },
    shareMilkDesc: {
      ar: "توفير الحليب والتغذية الأساسية لرضيع لمدة شهر كامل",
      fr: "Fournir du lait et une nutrition de base à un nourrisson pendant un mois",
      en: "Provide milk and basic nutrition for an infant for a full month"
    },
    shareDiapers: {
      ar: "سهم الحفاضات",
      fr: "Part des Couches",
      en: "Diapers Share"
    },
    shareDiapersDesc: {
      ar: "تغطية احتياجات الحفاضات ومستلزمات النظافة لطفل لمدة شهر",
      fr: "Couvrir les besoins en couches et produits d'hygiène d'un enfant pendant un mois",
      en: "Cover diaper and hygiene needs for a child for one month"
    },
    shareCaregiver: {
      ar: "سهم أجرة مربية",
      fr: "Part Salaire Éducatrice",
      en: "Caregiver Share"
    },
    shareCaregiverDesc: {
      ar: "المساهمة في أجرة مربية متخصصة في رعاية الأطفال",
      fr: "Contribuer au salaire d'une éducatrice spécialisée en garde d'enfants",
      en: "Contribute to a specialized childcare educator's salary"
    },
    shareMedical: {
      ar: "سهم الرعاية الصحية",
      fr: "Part Soins Médicaux",
      en: "Medical Care Share"
    },
    shareMedicalDesc: {
      ar: "تغطية التطعيمات والفحوصات الطبية الدورية والأدوية",
      fr: "Couvrir les vaccinations, examens médicaux et médicaments",
      en: "Cover vaccinations, medical checkups and medications"
    },
    shareEducation: {
      ar: "سهم التعليم",
      fr: "Part Éducation",
      en: "Education Share"
    },
    shareEducationDesc: {
      ar: "توفير المستلزمات التعليمية والألعاب التنموية للأطفال",
      fr: "Fournir du matériel éducatif et des jeux de développement",
      en: "Provide educational supplies and developmental toys"
    },
    shareFullCare: {
      ar: "كفالة شاملة",
      fr: "Parrainage Complet",
      en: "Full Sponsorship"
    },
    shareFullCareDesc: {
      ar: "كفالة شاملة تغطي جميع احتياجات طفل واحد لمدة شهر كامل",
      fr: "Parrainage complet couvrant tous les besoins d'un enfant pendant un mois",
      en: "Complete sponsorship covering all needs of one child for a full month"
    },
    perMonth: {
      ar: "درهم / شهرياً",
      fr: "MAD / mois",
      en: "MAD / month"
    },
    transparencyTitle: {
      ar: "الشفافية المطلقة",
      fr: "Transparence Totale",
      en: "Total Transparency"
    },
    transparencySubtitle: {
      ar: "حساباتنا كالزجاج - نؤمن بحقك في معرفة أين يذهب كل درهم",
      fr: "Nos comptes sont transparents - Vous avez le droit de savoir où va chaque dirham",
      en: "Our accounts are transparent - You have the right to know where every dirham goes"
    },
    monthlyReport: {
      ar: "تقرير مالي شهري مفصل لكل متبرع",
      fr: "Rapport financier mensuel détaillé pour chaque donateur",
      en: "Detailed monthly financial report for each donor"
    },
    trackSpending: {
      ar: "تتبع دقيق لكل درهم يتم صرفه",
      fr: "Suivi précis de chaque dirham dépensé",
      en: "Precise tracking of every dirham spent"
    },
    openDoor: {
      ar: "سياسة الباب المفتوح - زيارة المركز متاحة للكفلاء",
      fr: "Politique de porte ouverte - Visite du centre accessible aux parrains",
      en: "Open door policy - Center visits available for sponsors"
    },
    childrenCared: {
      ar: "طفل تحت رعايتنا",
      fr: "enfants sous notre garde",
      en: "children in our care"
    },
    donateNow: {
      ar: "تبرع الآن",
      fr: "Faire un don",
      en: "Donate Now"
    },
    selectAmount: {
      ar: "اختر مبلغ التبرع",
      fr: "Sélectionnez le montant",
      en: "Select amount"
    },
    custom: {
      ar: "مبلغ آخر",
      fr: "Autre montant",
      en: "Custom"
    },
    inputPlaceholder: {
      ar: "أدخل المبلغ بالدرهم",
      fr: "Entrez le montant en MAD",
      en: "Enter amount in MAD"
    },
    donate: {
      ar: "أكمل التبرع",
      fr: "Compléter le don",
      en: "Complete Donation"
    },
    taxInfo: {
      ar: "جميع التبرعات مخصصة حصرياً لرعاية الأطفال المتخلى عنهم في مركزنا. نلتزم بالشفافية الكاملة في إدارة أموالكم.",
      fr: "Tous les dons sont exclusivement dédiés aux soins des enfants abandonnés dans notre centre. Nous nous engageons à une transparence totale.",
      en: "All donations are exclusively dedicated to caring for abandoned children in our center. We commit to full transparency."
    },
    success: {
      ar: "تمت عملية التبرع بنجاح!",
      fr: "Don effectué avec succès !",
      en: "Donation successful!"
    },
    thankYou: {
      ar: "شكراً لدعمك",
      fr: "Merci pour votre soutien",
      en: "Thank you for your support"
    },
    tabShares: {
      ar: "أسهم الكفالة",
      fr: "Parts de Parrainage",
      en: "Sponsorship Shares"
    },
    tabTransparency: {
      ar: "الشفافية",
      fr: "Transparence",
      en: "Transparency"
    },
    tabDonate: {
      ar: "تبرع",
      fr: "Donner",
      en: "Donate"
    },
    joinCircle: {
      ar: "انضم إلى دائرة السند",
      fr: "Rejoignez le Cercle de Soutien",
      en: "Join the Circle of Support"
    },
    joinCircleDesc: {
      ar: "كن جزءاً من عائلتنا الكبيرة. القليل الدائم من قلوب صادقة أفضل وأضمن من الكثير المنقطع.",
      fr: "Faites partie de notre grande famille. Un peu régulier de cœurs sincères vaut mieux que beaucoup d'intermittent.",
      en: "Be part of our big family. A little from sincere hearts consistently is better than a lot intermittently."
    },
    becomeASponsor: {
      ar: "كن كفيلاً الآن",
      fr: "Devenez Parrain",
      en: "Become a Sponsor"
    },
    currency: { ar: "د.م", fr: "MAD", en: "MAD" },
    securePayment: {
      ar: "دفع آمن ومشفر 100%",
      fr: "Paiement 100% sécurisé et crypté",
      en: "100% secure and encrypted payment"
    },
    chooseShare: {
      ar: "اختر هذا السهم",
      fr: "Choisir cette part",
      en: "Choose this share"
    },
    popular: {
      ar: "الأكثر طلباً",
      fr: "Le plus populaire",
      en: "Most Popular"
    },
    monthlyBadge: {
      ar: "",
      fr: "",
      en: ""
    },
    onceBadge: {
      ar: "مرة واحدة",
      fr: "Une fois",
      en: "One-time"
    },
    monthlyNote: {
      ar: "",
      fr: "",
      en: ""
    },
    share: {
      ar: "شارك",
      fr: "Partager",
      en: "Share"
    },
    shareVia: {
      ar: "شارك عبر",
      fr: "Partager via",
      en: "Share via"
    },
    shareWhatsapp: {
      ar: "واتساب",
      fr: "WhatsApp",
      en: "WhatsApp"
    },
    copyLink: {
      ar: "نسخ الرابط",
      fr: "Copier le lien",
      en: "Copy Link"
    },
    linkCopied: {
      ar: "تم نسخ الرابط بنجاح!",
      fr: "Lien copié avec succès!",
      en: "Link copied successfully!"
    },
    shareMessage: {
      ar: "معاً للأطفال في أمان | Ensemble pour les enfants en sécurité\nانضم إلى دائرة السند وساعد الأطفال المتخلى عنهم. تبرعك يصنع فرقاً كبيراً في حياتهم.\nRejoignez-nous pour aider les enfants abandonnés.",
      fr: "Ensemble pour les enfants en sécurité | معاً للأطفال في أمان\nRejoignez le Cercle de Soutien et aidez les enfants abandonnés. Votre don fait une grande différence dans leur vie.\nJoin us to help abandoned children.",
      en: "Together for children in safety | معاً للأطفال في أمان\nJoin the Circle of Support and help abandoned children. Your donation makes a big difference in their lives.\nRejoignez-nous pour aider les enfants abandonnés."
    }
  }), []);

  const getTranslation = useCallback((key) => {
    return translations[key]?.[language] || translations[key]?.['ar'] || '';
  }, [language, translations]);

  const shares = useMemo(() => [
    { id: 'milk', icon: FaChild, amount: 100, color: '#4FC3F7', gradient: 'linear-gradient(135deg, #4FC3F7 0%, #0288D1 100%)' },
    { id: 'diapers', icon: FaChild, amount: 150, color: '#81C784', gradient: 'linear-gradient(135deg, #81C784 0%, #388E3C 100%)' },
    { id: 'caregiver', icon: FaHandsHelping, amount: 200, color: '#FFB74D', gradient: 'linear-gradient(135deg, #FFB74D 0%, #F57C00 100%)', popular: true },
    { id: 'medical', icon: FaMedkit, amount: 250, color: '#E57373', gradient: 'linear-gradient(135deg, #E57373 0%, #D32F2F 100%)' },
    { id: 'education', icon: FaGraduationCap, amount: 180, color: '#9575CD', gradient: 'linear-gradient(135deg, #9575CD 0%, #512DA8 100%)' },
    { id: 'fullCare', icon: FaHome, amount: 2000, color: '#F06292', gradient: 'linear-gradient(135deg, #F06292 0%, #C2185B 100%)' },
  ], []);

  const shareTranslationKeys = useMemo(() => ({
    milk: { name: 'shareMilk', desc: 'shareMilkDesc' },
    diapers: { name: 'shareDiapers', desc: 'shareDiapersDesc' },
    caregiver: { name: 'shareCaregiver', desc: 'shareCaregiverDesc' },
    medical: { name: 'shareMedical', desc: 'shareMedicalDesc' },
    education: { name: 'shareEducation', desc: 'shareEducationDesc' },
    fullCare: { name: 'shareFullCare', desc: 'shareFullCareDesc' },
  }), []);

  useEffect(() => {
    if (window.paypal && activeTab === 'donate') {
      const paypalButtons = window.paypal.Buttons({
        style: { color: 'blue', shape: 'pill', label: 'pay', height: 50 },
        createOrder: function (data, actions) {
          let finalAmount = showCustom ? customAmount : selectedAmount;
          finalAmount = parseFloat(finalAmount).toFixed(2);
          return actions.order.create({
            purchase_units: [{ amount: { value: finalAmount, currency_code: "EUR" } }]
          });
        },
        onApprove: function (data, actions) {
          return actions.order.capture().then(function (details) {
            MySwal.fire({
              title: getTranslation('success'),
              text: `${getTranslation('thankYou')}, ${details.payer.name.given_name}!`,
              icon: 'success',
              confirmButtonText: 'OK',
            });
          });
        }
      });

      const container = document.getElementById('paypal-button-container');
      if (container) {
        container.innerHTML = '';
        paypalButtons.render('#paypal-button-container');
      }

      return () => {
        try { paypalButtons.close(); } catch (e) { console.error("Error closing PayPal buttons:", e); }
      };
    }
  }, [MySwal, showCustom, customAmount, selectedAmount, getTranslation, activeTab]);

  const handleAmountSelect = (value) => {
    setSelectedAmount(value);
    setShowCustom(false);
  };

  const handleShareSelect = (share) => {
    setSelectedShare(share.id);
    setSelectedAmount(String(share.amount));
    setShowCustom(false);
    setActiveTab('donate');
    window.scrollTo({ top: document.querySelector('.donner-tabs-section')?.offsetTop - 80, behavior: 'smooth' });
  };

  const handleCustomAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
  };

  return (
    <div className="donner-page" dir={isRtl ? 'rtl' : 'ltr'}>

      {/* ===== HERO SECTION ===== */}
      <section className="donner-hero">
        <div className="donner-hero-bg">
          <div className="donner-hero-overlay"></div>
          <img 
            src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1920&h=800&fit=crop" 
            alt="Children" 
            className="donner-hero-img"
          />
        </div>
        <div className="donner-hero-content">
          <div className="donner-hero-badge">
            <FaShieldAlt />
            <span>{getTranslation('heroSubtitle')}</span>
          </div>
          <h1 className="donner-hero-title">{getTranslation('heroTitle')}</h1>
          <p className="donner-hero-desc">{getTranslation('heroDescription')}</p>
          <div className="donner-hero-actions">
            <button className="donner-hero-btn primary" onClick={() => { setActiveTab('donate'); window.scrollTo({ top: document.querySelector('.donner-tabs-section')?.offsetTop - 80, behavior: 'smooth' }); }}>
              <FaDonate />
              <span>{getTranslation('donateNow')}</span>
            </button>
            <button className="donner-hero-btn secondary" onClick={() => { setActiveTab('shares'); window.scrollTo({ top: document.querySelector('.donner-tabs-section')?.offsetTop - 80, behavior: 'smooth' }); }}>
              <FaEye />
              <span>{getTranslation('tabShares')}</span>
            </button>
          </div>
        </div>
      </section>

      {/* ===== SIMPLE STAT ===== */}
      <section className="donner-stats">
        <div className="donner-stats-inner">
          <div className="donner-stats-grid donner-stats-single">
            <div className="donner-stat-card">
              <div className="donner-stat-icon" style={{ background: 'linear-gradient(135deg, #4FC3F7, #0288D1)' }}><FaChild /></div>
              <div className="donner-stat-number">4+</div>
              <div className="donner-stat-label">{getTranslation('childrenCared')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TABS SECTION ===== */}
      <section className="donner-tabs-section">
        <div className="donner-tabs-nav">
          <button className={`donner-tab-btn ${activeTab === 'shares' ? 'active' : ''}`} onClick={() => setActiveTab('shares')}>
            <FaHandHoldingHeart /> <span>{getTranslation('tabShares')}</span>
          </button>
          <button className={`donner-tab-btn ${activeTab === 'transparency' ? 'active' : ''}`} onClick={() => setActiveTab('transparency')}>
            <FaEye /> <span>{getTranslation('tabTransparency')}</span>
          </button>
          <button className={`donner-tab-btn ${activeTab === 'donate' ? 'active' : ''}`} onClick={() => setActiveTab('donate')}>
            <FaDonate /> <span>{getTranslation('tabDonate')}</span>
          </button>
        </div>

        <div className="donner-tab-content">

          {/* ===== SHARES TAB ===== */}
          {activeTab === 'shares' && (
            <div className="donner-shares-tab">
              <div className="donner-tab-header">
                <h2>{getTranslation('sharesTitle')}</h2>
                <p>{getTranslation('sharesSubtitle')}</p>
              </div>
              <div className="donner-shares-grid">
                {shares.map((share) => {
                  const keys = shareTranslationKeys[share.id];
                  const IconComp = share.icon;
                  return (
                    <div key={share.id} className={`donner-share-card ${selectedShare === share.id ? 'selected' : ''} ${share.popular ? 'popular' : ''}`}>
                      {share.popular && <div className="donner-popular-badge">{getTranslation('popular')}</div>}
                      <div className="donner-share-icon" style={{ background: share.gradient }}>
                        <IconComp />
                      </div>
                      <h3 className="donner-share-name">{getTranslation(keys.name)}</h3>
                      <p className="donner-share-desc">{getTranslation(keys.desc)}</p>
                      <div className="donner-share-price">
                        <span className="donner-share-amount">{share.amount}</span>
                        <span className="donner-share-currency">{getTranslation('perMonth')}</span>
                      </div>
                      <button className="donner-share-btn" style={{ background: share.gradient }} onClick={() => handleShareSelect(share)}>
                        <span>{getTranslation('chooseShare')}</span>
                        <ArrowIcon />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== TRANSPARENCY TAB ===== */}
          {activeTab === 'transparency' && (
            <div className="donner-transparency-tab">
              <div className="donner-tab-header">
                <h2>{getTranslation('transparencyTitle')}</h2>
                <p>{getTranslation('transparencySubtitle')}</p>
              </div>
              <div className="donner-transparency-content">
                <div className="donner-transparency-visual">
                  <img src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=600&h=400&fit=crop" alt="Transparency" />
                  <div className="donner-transparency-overlay">
                    <FaShieldAlt className="donner-shield-icon" />
                  </div>
                </div>
                <div className="donner-transparency-list">
                  <div className="donner-transparency-item">
                    <div className="donner-transparency-check"><FaCheckCircle /></div>
                    <p>{getTranslation('monthlyReport')}</p>
                  </div>
                  <div className="donner-transparency-item">
                    <div className="donner-transparency-check"><FaCheckCircle /></div>
                    <p>{getTranslation('trackSpending')}</p>
                  </div>
                  <div className="donner-transparency-item">
                    <div className="donner-transparency-check"><FaCheckCircle /></div>
                    <p>{getTranslation('openDoor')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== DONATE TAB ===== */}
          {activeTab === 'donate' && (
            <div className="donner-donate-tab">
              <div className="donner-tab-header">
                <h2>{getTranslation('donateNow')}</h2>
                <p>{getTranslation('selectAmount')}</p>
              </div>
              <div className="donner-donate-card">
                <div className="donner-donate-header">
                  <FaHandHoldingHeart className="donner-donate-icon" />
                  <h3>{getTranslation('selectAmount')}</h3>
                </div>

                {selectedShare && (
                  <div className="donner-selected-share-display">
                    <div className="donner-share-display-content">
                      <span className="donner-share-display-label">المبلغ المختار:</span>
                      <span className="donner-share-display-amount">{selectedAmount} {getTranslation('currency')}</span>
                    </div>
                  </div>
                )}

                <div className="donner-amount-grid">
                  {['50', '100', '150', '200', '300', '500', '2000'].map((amt) => (
                    <button
                      key={amt}
                      className={`donner-amount-btn ${selectedAmount === amt && !showCustom ? 'active' : ''}`}
                      onClick={() => handleAmountSelect(amt)}
                    >
                      {amt} {getTranslation('currency')}
                    </button>
                  ))}
                  <button
                    className={`donner-amount-btn custom-btn ${showCustom ? 'active' : ''}`}
                    onClick={() => setShowCustom(true)}
                  >
                    {getTranslation('custom')}
                  </button>
                </div>

                {showCustom && (
                  <div className="donner-custom-input-wrap">
                    <span className="donner-currency-symbol">{getTranslation('currency')}</span>
                    <input
                      type="text"
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      className="donner-custom-input"
                      placeholder={getTranslation('inputPlaceholder')}
                      dir="ltr"
                    />
                  </div>
                )}

                <div className="donner-payment-section">
                  <div className="donner-payment-label">
                    <FaCreditCard className="donner-pay-icon" />
                    <span>{getTranslation('donate')}</span>
                  </div>
                  <div id="paypal-button-container" className="donner-paypal-btn"></div>
                </div>

                <div className="donner-secure-badge">
                  <FaShieldAlt />
                  <span>{getTranslation('securePayment')}</span>
                </div>

                <div className="donner-info-box">
                  <FaInfoCircle className="donner-info-icon" />
                  <p>{getTranslation('taxInfo')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="donner-cta">
        <div className="donner-cta-inner">
          <FaHeart className="donner-cta-icon" />
          <h2>{getTranslation('joinCircle')}</h2>
          <p>{getTranslation('joinCircleDesc')}</p>
          <button className="donner-cta-btn" onClick={() => { setActiveTab('donate'); window.scrollTo({ top: document.querySelector('.donner-tabs-section')?.offsetTop - 80, behavior: 'smooth' }); }}>
            <FaDonate />
            <span>{getTranslation('becomeASponsor')}</span>
          </button>
        </div>
      </section>

    </div>
  );
};

export default Donner;